'use client';

import { useState } from 'react';
import { useFormatter, useTranslations } from 'next-intl';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTRPC } from '@/lib/trpc/provider';
import { useViewerTimezone } from '@/hooks/use-local-time-range';
import { getTimezoneAbbreviation } from '@/lib/timezone-utils';
import {
  ANOTHER_TIME_BANDS,
  ANOTHER_TIME_WEEK_ORDER,
  buildAnotherTimeSlots,
  type AnotherTimeBandKey,
} from '@/lib/enrollment/another-time-bands';

export type AnotherTimeRequestProps = {
  courseId: string;
  /** The term whose classes the family was shown — and ruled out. */
  termId: string;
  childProfileId: string;
  /** The host's eligibility block. The host renders the reason; this only stops. */
  blocked: boolean;
  onSubmitted: () => void;
  onPendingChange?: (pending: boolean) => void;
};

function toggle<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

/**
 * "When can your child attend?" — rendered IN PLACE OF the pay / submit controls
 * once a family picks "None of these times work for us" in `ClassPicker`.
 *
 * Sends an enrolment request flagged `wantsAnotherTime`, pinned to the term whose
 * classes were on screen, carrying the ticked times and the zone they were shown
 * in. It names NO class: the family has just ruled every one of them out.
 *
 * The host form owns the submitted state (it swaps itself for a confirmation on
 * `onSubmitted`) and locks its class list while `onPendingChange(true)` holds.
 */
export function AnotherTimeRequest({
  courseId,
  termId,
  childProfileId,
  blocked,
  onSubmitted,
  onPendingChange,
}: AnotherTimeRequestProps) {
  const t = useTranslations('public.enrollment.form');
  const tDays = useTranslations('common.dayShort');
  const format = useFormatter();
  // The zone the page shows class times in — so the zone the family reads the
  // bands in, and the zone the request records beside them.
  const viewerTz = useViewerTimezone();
  const trpc = useTRPC();
  const [days, setDays] = useState<number[]>([]);
  const [bands, setBands] = useState<AnotherTimeBandKey[]>([]);

  // One LITERAL key per day: the dynamic-key guard refuses `tDays(variable)`.
  const dayLabel: Record<number, string> = {
    1: tDays('mon'),
    2: tDays('tue'),
    3: tDays('wed'),
    4: tDays('thu'),
    5: tDays('fri'),
    6: tDays('sat'),
    0: tDays('sun'),
  };

  // Band bounds in the viewer's locale ("5 PM", "17:00", "下午5时"). The date is
  // a constant and only its UTC hour and minute are read — no clock in render.
  const clock = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return format.dateTime(new Date(Date.UTC(2024, 0, 1, h, m)), {
      hour: 'numeric',
      ...(m ? { minute: '2-digit' } : {}),
      timeZone: 'UTC',
    });
  };
  const bandLabel = (key: AnotherTimeBandKey, start: string, end: string): string => {
    const bounds = { start: clock(start), end: clock(end) };
    switch (key) {
      case 'morning':
        return t('bandMorning', bounds);
      case 'afternoon':
        return t('bandAfternoon', bounds);
      case 'evening':
        return t('bandEvening', bounds);
    }
  };

  const submitMutation = useMutation(
    trpc.enrollment.submitEnrollmentRequest.mutationOptions({
      onMutate: () => {
        onPendingChange?.(true);
      },
      onSettled: () => {
        onPendingChange?.(false);
      },
      onSuccess: () => {
        onSubmitted();
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );
  const pending = submitMutation.isPending;
  const incomplete = days.length === 0 || bands.length === 0;

  const handleSubmit = () => {
    if (!childProfileId || blocked || pending || incomplete) return;
    submitMutation.mutate({
      courseId,
      childProfileId,
      wantsAnotherTime: true,
      termId,
      availabilityTimezone: viewerTz,
      availabilitySlots: buildAnotherTimeSlots(days, bands),
    });
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-3" data-testid="another-time-request">
      <div>
        <p className="text-sm font-medium text-gray-700">{t('whenCanAttend')}</p>
        <p className="text-xs text-[#6b7280] mt-0.5" data-testid="another-time-timezone">
          {t('timesInYourTimezone', { tz: getTimezoneAbbreviation(viewerTz) })}
        </p>
      </div>

      <fieldset disabled={pending}>
        <legend className="text-xs font-medium text-gray-700 mb-1">{t('anotherTimeDays')}</legend>
        <div className="flex flex-wrap gap-2">
          {ANOTHER_TIME_WEEK_ORDER.map((day) => (
            <label
              key={day}
              className="flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-sm cursor-pointer"
            >
              <input
                type="checkbox"
                data-testid={`another-time-day-${day}`}
                checked={days.includes(day)}
                onChange={() => setDays((prev) => toggle(prev, day))}
              />
              {dayLabel[day]}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset disabled={pending}>
        <legend className="text-xs font-medium text-gray-700 mb-1">{t('anotherTimeTimes')}</legend>
        <div className="space-y-1.5">
          {ANOTHER_TIME_BANDS.map((band) => (
            <label key={band.key} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid={`another-time-band-${band.key}`}
                checked={bands.includes(band.key)}
                onChange={() => setBands((prev) => toggle(prev, band.key))}
              />
              {bandLabel(band.key, band.start, band.end)}
            </label>
          ))}
        </div>
      </fieldset>

      <Button
        onClick={handleSubmit}
        disabled={!childProfileId || blocked || pending || incomplete}
        className="min-h-[44px] min-w-[44px] w-full bg-eifo-orange hover:bg-eifo-orange/85 text-white font-bold"
      >
        {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('askToJoinWaitlist')}
      </Button>
      {incomplete && (
        <p className="text-xs text-[#6b7280]" data-testid="another-time-hint">
          {t('pickDayAndTimeHint')}
        </p>
      )}
    </div>
  );
}
